import React, { useState, useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import Layout from './Layout';
import IndexCampaigns from './campaigns/Index';
import NewCampaign from './campaigns/New';
import DealsList from './deals/list/DealsList.view';
import DealFormPage from './deals/form/DealFormPage';
import ProfileStatsPage from './profile/ProfileStatsPage';
import ProfileAboutPage from './profile/ProfileAboutPage';
import Scorecard from './scorecard/Scorecard';
import NotFound from './NotFound';
import Dashboard from './dashboards/Dashboard.view';
import SelectChart from './charts/SelectChart';
import NewReward from './rewards/NewReward';
import UsersScreen from './users/UsersScreen';
import OrgsList from './config/OrgsList';
import OrgConfigPage from './config/OrgConfigPage';
import Games from './games';
import DevComp from './config/DevComp';
import TrainingHomePage from './training/TrainingHomePage';
import TrainingCategoryPage from './training/TrainingCategoryPage';
import TrainingVideoPage from './training/TrainingVideoPage';
import Feed from './feed';
import Comments from './feed/Comments';
import NewIncentive from './games/NewIncentive';
import RewardsList from './rewards/RewardsList';
import TeamsScreen from './teams/TeamsScreen';
import TeamPage from './teams/TeamPage';
import TeamFormPage from './teams/TeamFormPage';
import RewardDetails from './rewards/RewardDetails';
import ReportsScreen from './reports/ReportsScreen';
import DataImports from './data-imports/DataImports';
import UserFormPage from './users/UserFormPage';
import PerformanceSideBar from './performance/PerformanceSideBar.view';
import ProductsScreen from './products/ProductsScreen';
import DealsKanban from './deals/kanban/DealsKanban.view';
import PipelineScreen from './pipeline/PipelineScreen.view';
import PaymentsSideBar from './payments/PaymentsSideBar.view';
import Transactions from './payments/paymentsTabs/Transactions.view';
import BatchSettlements from './payments/paymentsTabs/BatchSettlements.view';
import Payouts from './payments/paymentsTabs/Payouts.view';
import MerchantStatements from './payments/paymentsTabs/MerchantStatements.view';
import MerchantReports from './payments/paymentsTabs/MerchantReports';
import { SettingsPage } from './settings/SettingsPage';
import { SchedulingScreen } from './scheduling/SchedulingScreen';
import Leaderboard from './leaderboard';
import { Typography, Box } from '@mui/material';
import { APP_VERSION } from '../constants/version';
import ChangelogModal from '../components/ChangelogModal';
import ActivityAnalytics from './activity';
import GameDetailsView from './games/GameDetails/GameDetails.view';
import MapPage from './map/MapPage';
import FeatureFlagRoute from '../common/FeatureFlagRoute';
import WebInterfaceRoute from '../common/WebInterfaceRoute';
import RewardsArchiveView from './performance/RewardsArchive';
import { V2Layout } from '../../v2/layouts/V2Layout';
import { V2MobileNav } from '../../v2/components/v2-mobile-nav';
import {
  PipelinePage as V2PipelinePage,
  DealFormPage as V2DealFormPage,
} from '../../v2/pages/pipeline';
import {
  PeoplePage as V2PeoplePage,
  PersonDetailPage as V2PersonDetailPage,
  PersonFormPage as V2PersonFormPage,
  BulkLeadImportPage as V2BulkLeadImportPage,
} from '../../v2/pages/people';
import {
  TeamsPage as V2TeamsPage,
  TeamDetailPage as V2TeamDetailPage,
  TeamFormPage as V2TeamFormPage,
} from '../../v2/pages/teams';
import {
  CustomerFacingProfilePage as V2CustomerFacingProfilePage,
  CompanyInformationPage as V2CompanyInformationPage,
  GeneralInformationPage as V2GeneralInformationPage,
  DamageReportConfigPage as V2DamageReportConfigPage,
  SalesToolsPage as V2SalesToolsPage,
  TaskListManagementPage as V2TaskListManagementPage,
  BillingPage as V2BillingPage,
  DashboardConfigPage as V2DashboardConfigPage,
  AISettingsPage as V2AISettingsPage,
  DocuSealVisibilityPage as V2DocuSealVisibilityPage,
} from '../../v2/pages/settings';
import {
  V2TrainingHomePage,
  V2TrainingCategoryPage,
  V2TrainingVideoPage,
} from '../../v2/pages/training';
import {
  LeaderboardPage as V2LeaderboardPage,
  RewardsPage as V2RewardsPage,
  RewardDetailsPage as V2RewardDetailsPage,
  RewardFormPage as V2RewardFormPage,
  RewardsArchivePage as V2RewardsArchivePage,
  ActivityPage as V2ActivityPage,
} from '../../v2/pages/performance';
import { PaymentsPage as V2PaymentsPage } from '../../v2/pages/payments';
import { MapPage as V2MapPage } from '../../v2/pages/map';
import { FeatureFlagsPage as V2FeatureFlagsPage } from '../../v2/pages/admin-config';
import {
  CancellationGatePage as V2CancellationGatePage,
  CancellationConfirmedPage as V2CancellationConfirmedPage,
  ExitSurveyPage as V2ExitSurveyPage,
} from '../../v2/pages/settings/cancellation';
import { ProductsPage as V2ProductsPage } from '../../v2/pages/products';
import DealDetailPage from './deal/DealDetailPage';
import { isStarterAccount } from '../common/utils/isStarterAccount';
import useOrgStore from '../store/zustand/useOrgStore';
import OrgUserIndicator from '../components/OrgUserIndicator';
import useOrgData from '../hooks/useOrgData';
import { Auth } from 'aws-amplify';

const FIRST_LOGIN_REDIRECTED_KEY = 'paiv_first_login_redirected';
const V2_OPT_IN_KEY = 'paiv_v2_opt_in';

function InitialRedirect() {
  const navigate = useNavigate();
  const [hasRedirected, setHasRedirected] = useState(false);
  const orgData = useOrgStore((state) => state.orgData);

  useEffect(() => {
    // @ts-ignore
    if ((global as any)?.me?.id && !hasRedirected) {
      const checkFirstLogin = async () => {
        try {
          const alreadyRedirected = localStorage.getItem(
            FIRST_LOGIN_REDIRECTED_KEY
          );

          if (!alreadyRedirected) {
            const cognitoUser = await Auth.currentAuthenticatedUser();
            const signupVersion =
              cognitoUser.signInUserSession?.idToken?.payload?.[
                'custom:signupVersion'
              ];

            // Users from self-signup have signupVersion set (2.0 or 2.1)
            if (signupVersion) {
              localStorage.setItem(FIRST_LOGIN_REDIRECTED_KEY, 'true');
              navigate('/settings/company', { replace: true });
              setHasRedirected(true);
              return;
            }
          }
        } catch (err) {
          console.error('Error checking first login redirect:', err);
        }

        // Seed orgs (paivcam-seed) always go to V2
        if (isStarterAccount()) {
          navigate('/v2/pipeline', { replace: true });
          setHasRedirected(true);
          return;
        }

        // Existing users who opted in to V2
        const optedIn = localStorage.getItem(V2_OPT_IN_KEY) === 'true';
        if (optedIn) {
          navigate('/v2/pipeline', { replace: true });
        } else {
          // Default: send to V2 pipeline (all users get V2 by default now)
          navigate('/v2/pipeline', { replace: true });
        }
        setHasRedirected(true);
      };

      checkFirstLogin();
    }
    // @ts-ignore
  }, [(global as any)?.me?.id, (global as any)?.me?.clonedFrom, orgData]);

  return null;
}

export default function RouteConfiguration() {
  return (
    <BrowserRouter>
      <RouteWithBG />
    </BrowserRouter>
  );
}

function RouteWithBG() {
  const [openChangelog, setOpenChangelog] = useState(false);
  const location = useLocation();
  const isV2Route = location.pathname.startsWith('/v2');

  // Fetch org data at the app root so it's available globally (e.g. OrgUserIndicator).
  // The hook is idempotent — Settings pages that also call it will find data already in the store.
  useOrgData();

  return (
    <Box sx={(theme) => ({ backgroundColor: theme.custom?.background1 })}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<InitialRedirect />} />
          <Route path="performance" element={<PerformanceSideBar />}>
            <Route path="leaderboard" element={<Leaderboard />} />
            <Route path="games" element={<Games />} />
            <Route path="games/:gameId" element={<GameDetailsView />} />
            <Route path="games/new-incentive" element={<NewIncentive />} />
            <Route path="rewards" element={<RewardsList />} />
            <Route path="rewards/new" element={<NewReward />} />
            <Route path="rewards/edit/:rewardId" element={<NewReward />} />
            <Route path="rewards/details" element={<RewardDetails />} />
            <Route path="rewards/archive" element={<RewardsArchiveView />} />
            <Route path="activity" element={<ActivityAnalytics />} />
            <Route path="reports" element={<ReportsScreen />} />
          </Route>
          <Route path="scorecard/:userId" element={<Scorecard />} />
          <Route path="campaigns" element={<IndexCampaigns />} />
          <Route
            path="charts"
            // @ts-ignore
            element={<SelectChart userID={global?.me?.id} />}
          />
          <Route path="training" element={<TrainingHomePage />} />
          <Route path="training/:category" element={<TrainingCategoryPage />} />
          <Route
            path="training/:category/:lessonId"
            element={<TrainingVideoPage />}
          />
          <Route path="devtool" element={<DevComp />} />
          <Route path="pipeline" element={<PipelineScreen />}>
            <Route path="board" element={<DealsKanban />} />
            <Route path="board/:dealId" element={<DealsKanban />} />
            <Route path="list" element={<DealsList />} />
            <Route path="list/:dealId" element={<DealsList />} />
            <Route path="new" element={<DealFormPage />} />
            <Route path="edit/:dealId" element={<DealFormPage />} />
          </Route>
          <Route element={<WebInterfaceRoute flag="hideFeed" />}>
            <Route path="feed" element={<Feed />} />
            <Route path="feed/comments" element={<Comments />} />
          </Route>
          <Route path="campaigns/new" element={<NewCampaign />} />
          <Route path="people" element={<UsersScreen />} />
          <Route path="people/new" element={<UserFormPage />} />
          <Route path="people/edit/:userId" element={<UserFormPage />} />
          <Route path="people/stats/:userId" element={<ProfileStatsPage />} />
          <Route path="people/about/:userId" element={<ProfileAboutPage />} />
          <Route path="teams" element={<TeamsScreen />} />
          <Route path="teams/:teamId" element={<TeamPage />} />
          <Route path="teams/new" element={<TeamFormPage />} />
          <Route path="teams/edit/:teamId" element={<TeamFormPage />} />
          <Route element={<WebInterfaceRoute flag="hideProducts" />}>
            <Route path="products" element={<ProductsScreen />} />
          </Route>
          <Route path="dashboard" element={<Dashboard />} />
          <Route element={<FeatureFlagRoute featureName="webMap" />}>
            <Route path="map" element={<MapPage />} />
          </Route>
          <Route path="payments" element={<PaymentsSideBar />}>
            <Route path="transactions" element={<Transactions />} />
            <Route path="batch-settlements" element={<BatchSettlements />} />
            <Route path="payouts" element={<Payouts />} />
            <Route
              path="merchant-statements"
              element={<MerchantStatements />}
            />
            <Route path="merchant-reports" element={<MerchantReports />} />
          </Route>
          <Route path="admin-config" element={<OrgsList />} />
          <Route path="admin-config/:orgID" element={<OrgConfigPage />} />
          <Route path="imports" element={<DataImports />} />
          <Route
            path="/settings"
            element={<Navigate to="/settings/general" replace />}
          />
          <Route path="settings/*" element={<SettingsPage />} />
          <Route element={<WebInterfaceRoute flag="disableScheduling" />}>
            <Route path="scheduling" element={<SchedulingScreen />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
        {/* V2 Routes - New shadcn/ui based screens */}
        <Route path="/v2" element={<V2Layout />}>
          <Route index element={<Navigate to="/v2/pipeline" replace />} />
          <Route path="home" element={<Navigate to="/v2/pipeline" replace />} />
          <Route path="pipeline" element={<V2PipelinePage />} />
          <Route path="pipeline/new" element={<V2DealFormPage />} />
          <Route path="pipeline/:dealId/edit" element={<V2DealFormPage />} />
          <Route path="pipeline/:dealId" element={<V2PipelinePage />} />
          <Route path="people" element={<V2PeoplePage />} />
          <Route path="people/new" element={<V2PersonFormPage />} />
          <Route path="people/edit/:userId" element={<V2PersonFormPage />} />
          <Route path="people/:userId" element={<V2PersonDetailPage />} />
          <Route path="people/:userId/:tab" element={<V2PersonDetailPage />} />
          <Route path="people/import" element={<V2BulkLeadImportPage />} />
          <Route
            path="settings/profile"
            element={<V2CustomerFacingProfilePage />}
          />
          <Route
            path="settings/general"
            element={<V2GeneralInformationPage />}
          />
          <Route
            path="settings/company"
            element={<V2CompanyInformationPage />}
          />
          <Route
            path="settings/damage-reports"
            element={<V2DamageReportConfigPage />}
          />
          <Route path="settings/sales-tools" element={<V2SalesToolsPage />} />
          <Route
            path="settings/task-list"
            element={<V2TaskListManagementPage />}
          />
          <Route path="settings/billing" element={<V2BillingPage />} />
          <Route
            path="settings/billing/cancel"
            element={<V2CancellationGatePage />}
          />
          <Route
            path="settings/billing/cancel/confirmed"
            element={<V2CancellationConfirmedPage />}
          />
          <Route
            path="settings/billing/cancel/survey"
            element={<V2ExitSurveyPage />}
          />
          <Route
            path="settings/dashboard"
            element={<V2DashboardConfigPage />}
          />
          <Route path="settings/ai" element={<V2AISettingsPage />} />
          <Route
            path="settings/docuseal"
            element={<V2DocuSealVisibilityPage />}
          />
          <Route path="products" element={<V2ProductsPage />} />
          <Route path="teams" element={<V2TeamsPage />} />
          <Route path="teams/new" element={<V2TeamFormPage />} />
          <Route path="teams/edit/:teamId" element={<V2TeamFormPage />} />
          <Route path="teams/:teamId" element={<V2TeamDetailPage />} />
          <Route path="training" element={<V2TrainingHomePage />} />
          <Route
            path="training/:category"
            element={<V2TrainingCategoryPage />}
          />
          <Route
            path="training/:category/:lessonId"
            element={<V2TrainingVideoPage />}
          />
          <Route
            path="performance/leaderboard"
            element={<V2LeaderboardPage />}
          />
          <Route path="performance/rewards" element={<V2RewardsPage />} />
          <Route
            path="performance/rewards/new"
            element={<V2RewardFormPage />}
          />
          <Route
            path="performance/rewards/edit/:rewardId"
            element={<V2RewardFormPage />}
          />
          <Route
            path="performance/rewards/details"
            element={<V2RewardDetailsPage />}
          />
          <Route
            path="performance/rewards/archive"
            element={<V2RewardsArchivePage />}
          />
          <Route
            path="performance/reports"
            element={
              <div
                style={{
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <V2MobileNav />
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  <ReportsScreen />
                </div>
              </div>
            }
          />
          <Route path="performance/activity" element={<V2ActivityPage />} />
          <Route
            path="performance/games"
            element={
              <div
                style={{
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <V2MobileNav />
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  <Games />
                </div>
              </div>
            }
          />
          <Route
            path="performance/games/:gameId"
            element={
              <div
                style={{
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <V2MobileNav />
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  <GameDetailsView />
                </div>
              </div>
            }
          />
          <Route
            path="performance/games/new-incentive"
            element={
              <div
                style={{
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <V2MobileNav />
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  <NewIncentive />
                </div>
              </div>
            }
          />
          <Route path="payments" element={<V2PaymentsPage />} />
          <Route
            path="feed"
            element={
              <div
                style={{
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{ flex: 1, overflowY: 'auto' }}
                  className="v2-feed-wrap"
                >
                  <Feed />
                </div>
                <style>{`
                  .v2-feed-wrap > div {
                    padding: 16px !important;
                    align-items: stretch !important;
                  }
                  @media (min-width: 640px) {
                    .v2-feed-wrap > div {
                      padding: 24px 40px !important;
                      align-items: center !important;
                    }
                  }
                  .v2-feed-wrap > div > div,
                  .v2-feed-wrap > div > div > div > div {
                    width: 100% !important;
                    max-width: 760px !important;
                  }
                  .v2-feed-wrap .infinite-scroll-component__outerdiv,
                  .v2-feed-wrap .infinite-scroll-component {
                    width: 100% !important;
                    max-width: 760px !important;
                  }
                  .v2-feed-wrap img[alt="PAIV"] {
                    display: none !important;
                  }
                  .v2-feed-wrap .MuiBox-root:has(> img[alt="PAIV"]) {
                    height: auto !important;
                    background-color: transparent !important;
                    padding: 40px 0 !important;
                    min-height: 0 !important;
                  }
                `}</style>
              </div>
            }
          />
          <Route
            path="feed/comments"
            element={
              <div
                style={{
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{ flex: 1, overflowY: 'auto' }}
                  className="v2-feed-wrap"
                >
                  <Comments />
                </div>
                <style>{`
                  .v2-feed-wrap > div {
                    padding: 16px !important;
                    align-items: stretch !important;
                  }
                  @media (min-width: 640px) {
                    .v2-feed-wrap > div {
                      padding: 24px 40px !important;
                      align-items: center !important;
                    }
                  }
                  .v2-feed-wrap > div > div,
                  .v2-feed-wrap > div > div > div > div {
                    width: 100% !important;
                    max-width: 760px !important;
                  }
                `}</style>
              </div>
            }
          />
          <Route
            path="admin-config/feature-flags"
            element={<V2FeatureFlagsPage />}
          />
          <Route path="map" element={<V2MapPage />} />
          <Route
            path="scheduling"
            element={
              <>
                <style>{`
                  .v2-scheduling-wrap .MuiPaper-root { margin-top: 0 !important; }
                  .v2-scheduling-wrap > div > .MuiBox-root:last-of-type { margin-top: 0 !important; }
                  .v2-scheduling-mobile-msg { display: none; }
                  @media (max-width: 768px) {
                    .v2-scheduling-mobile-msg { display: flex; }
                    .v2-scheduling-wrap { display: none !important; }
                  }
                `}</style>
                <div
                  style={{
                    height: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <V2MobileNav />
                  <div
                    className="v2-scheduling-mobile-msg"
                    style={{
                      flex: 1,
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '40px 24px',
                      textAlign: 'center',
                      gap: '12px',
                    }}
                  >
                    <span style={{ fontSize: '40px' }}>📅</span>
                    <h2
                      style={{
                        fontSize: '20px',
                        fontWeight: 700,
                        color: 'hsl(var(--foreground))',
                        margin: 0,
                      }}
                    >
                      Scheduling works better on desktop
                    </h2>
                    <p
                      style={{
                        fontSize: '14px',
                        color: 'hsl(var(--muted-foreground))',
                        maxWidth: '320px',
                        margin: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      Some scheduling features may not work properly in mobile
                      browsers. For the best experience, use a desktop browser
                      or the PAIV mobile app.
                    </p>
                  </div>
                  <div
                    style={{ flex: 1, overflow: 'hidden' }}
                    className="v2-scheduling-wrap"
                  >
                    <SchedulingScreen />
                  </div>
                </div>
              </>
            }
          />
        </Route>
        <Route path="deal" element={<DealDetailPage />} />
        <Route path="deal/:dealId" element={<DealDetailPage />} />
      </Routes>
      {!isV2Route && (
        <>
          <Box
            sx={(theme) => ({
              textAlign: 'center',
              pb: 1,
              pt: 1,
              backgroundColor:
                theme.custom?.background1 ||
                theme.palette?.background?.default ||
                'inherit',
              color:
                theme.custom?.textLight ||
                theme.palette?.text?.secondary ||
                'inherit',
              '.dark &': {
                backgroundColor: 'hsl(0 0% 7%)',
                color: 'hsl(0 0% 65%)',
              },
            })}
          >
            <OrgUserIndicator />
            <Typography
              sx={(theme) => ({
                fontSize: 12,
                margin: 1,
                cursor: 'pointer',
                color: theme.custom?.textLight,
                '&:hover': {
                  textDecoration: 'underline',
                },
              })}
              onClick={() => setOpenChangelog(true)}
            >
              {APP_VERSION}
            </Typography>
          </Box>
          <ChangelogModal
            open={openChangelog}
            onClose={() => setOpenChangelog(false)}
          />
        </>
      )}
    </Box>
  );
}
